'use client';

// ПАПКИ «ЧАТОВ» (ТЗ M.3/M.4, владелец 09.09.2026, как в Telegram). Вместо воронки-фильтра
// над списком переписок — полоса папок: ОДНА стеклянная капсула, внутри слева шестерёнка
// настроек, дальше слова папок со счётчиком непрочитанных (владелец 10.09.2026, BACKLOG
// «обход приложения iOS 10.09» п.18; прежние белые капсулы на каждую папку отменены).
// Папка — набор условий, который человек собрал сам; прежние три пункта
// воронки («Личные», «Все», «Без автоматизации») стали обычными папками по умолчанию.
//
// ОДНА РУКА НА ВЕБ И ПРИЛОЖЕНИЕ: условия, слова и запись — здесь; вид в браузере рисует
// этот же файл (полоса + шторка form-kit), а в приложении те же настройки собираются
// МОДЕЛЯМИ СТРАНИЦ «Аккаунта» (NativeAccountPage) и уезжают полушторкой
// msgsFoldersSheet. Разбор условий в отбор списка живёт на сервере (@scriptera/db,
// folderWhere) — второй копии правил в кабинете нет.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
// Условия папки — из ЧИСТОГО модуля пакета базы (подпуть): барабан Prisma в браузер
// не тянем, а правила у сервера и кабинета обязаны быть одни.
import { folderRuleCount, type FolderRules } from '@scriptera/db/dialog-folders';
import { KActionBar, KBareButton, KButton, KInput, KListGroup, KListRow, KSwitch } from '@scriptera/ui/kit';
import { readLayoutMode, readPhoneLook, usePhoneLook } from './layout-mode';
import { folderAccountsWord } from '@/lib/folder-accounts-word';
import { usePersistentData } from '@/lib/use-persistent-data';
import { nativeMethod, type NativeAccountField, type NativeAccountPage, type NativeMsgsFoldersData } from '@/lib/native-app';
import { useNativePages, type NativePagesNames } from '@/lib/native-pages';
import { ChannelIcon, channelHex, nativeChannelTile } from './bot-templates';
import { useNativePhotos } from './native-photos';
import { shortAccountName } from '@/lib/account-folder-name';
import { FBlock, FChoiceManyBlock, FHint, FIn, FNotifyList } from './form-kit';
import { DEditList, DHero } from './drawer-kit';
import { foldersAfterRead } from './messages-read';
import type { StripDrive } from './folder-pager';
import { PickPage } from './audience-filter-sheet';
import { askConfirm } from './ask';
import { folderPickStore, foldersApi, useFolderCount } from './folder-store';
import { useSlidePanel } from './native-layer-host';
import { accountRoleHex, closeNativeSettingsPage, openNativeSettingsPage } from '@/lib/account-native';
import type { DialogAccount } from './messages';

/**
 * АККАУНТ ПАПКИ (владелец 12.09.2026, П65): папка помечена «показывать как аккаунт» —
 * в полосе вместо слова стоят два кружка: значок сети и фотография аккаунта. Имя папки
 * при этом никуда не девается: оно остаётся в настройках и в подсказке доступности.
 * Поле НОВОЕ и необязательное: метки нет — папка рисуется словом, как раньше.
 */
export interface FolderAccount {
  key: string;
  channel: 'tg' | 'ig';
  /** Адрес НАШЕЙ ручки фотографии (`/api/cabinet/accounts/<credId>/avatar`); пусто — буква. */
  avatarUrl?: string;
}

/** Папка в том виде, в каком её отдаёт /api/cabinet/messages/folders. */
export interface DialogFolder {
  id: string;
  name: string;
  position: number;
  rules: FolderRules;
  /** Сколько переписок с непрочитанным попадает в папку — цифра на капсуле. */
  unread: number;
  /** Аккаунт помеченной папки (П65); пусто — обычная папка. */
  account?: FolderAccount;
  /** Ключ аккаунта, которым папка помечена (П65): по нему стоит тумблер в редакторе. */
  accountKey?: string | null;
  /** «Только с перепиской» и пусто (П156 п.2): в полосе папки нет, в списке за
   *  шестерёнкой — есть. */
  hidden?: boolean;
}

interface FoldersResp {
  ok: boolean;
  folders: DialogFolder[];
}

/** Какая папка активна — ПАМЯТЬ УСТРОЙСТВА (ТЗ M.1): на сервере её не держим. */
// ВЫБРАННАЯ ПАПКА — ОДНА НА ВЕСЬ РАЗДЕЛ (память устройства + общий store, folder-store.ts):
// хук зовут в нескольких местах (полоса, шторка настроек, редактор), и своя useState у
// каждого разошлась бы. Та же механика у папок «Контактов» (владелец 10.09.2026).
const FOLDERS_URL = '/api/cabinet/messages/folders';
// «Чаты» открываются на первой папке человека, память папки — только на сессию (19.09.2026).
const pickStore = folderPickStore('scr-msgs-folder', false);
const api = foldersApi<FolderRules, DialogFolder>(FOLDERS_URL);

export interface FoldersHand {
  /** ВСЕ папки — список за шестерёнкой и редактор. */
  folders: DialogFolder[];
  /** Папки ПОЛОСЫ (П156 п.2): без пустых «только с перепиской» (`hidden`). Полоса, пейджер
   *  приложения и активная папка живут этим списком — веб и приложение одинаково. */
  shown: DialogFolder[];
  /** id активной папки (из `shown`); пусто — папок нет вовсе, список идёт без отбора. */
  current: string;
  setCurrent: (id: string) => void;
  reload: () => void;
  /**
   * ПЕРЕПИСКУ ИЗ ПАПКИ ПРОЧИТАЛИ — НАКЛЕЙКА ОПАДАЕТ ТУТ ЖЕ (П14 обхода владельца
   * 11.09.2026). `was` — сколько сообщений у неё было не прочитано; не было вовсе (ноль) —
   * с наклейки снимать нечего. Считает чистая рука `foldersAfterRead` (messages-read.ts),
   * подмена штатная — mutate того же кэша, второго списка папок в кабинете нет.
   */
  readDialog: (folderId: string, was: number) => void;
  /** Папки уже приехали (первый ответ) — до этого полосу не рисуем, чтобы не мигала. */
  ready: boolean;
}

/**
 * ПАПКИ ЧЕЛОВЕКА И АКТИВНАЯ ПАПКА. Кэш общий (usePersistentData по адресу ручки), поэтому
 * полоса, шторка настроек и родные страницы видят одно и то же — второй копии списка в
 * кабинете нет. Активная папка живёт на устройстве; её удалили или папок ещё нет —
 * активной становится первая по порядку.
 */
export function useDialogFolders(): FoldersHand {
  const { data, reload, mutate } = usePersistentData<FoldersResp>('msgs-folders', FOLDERS_URL);
  const folders = useMemo(() => data?.folders ?? [], [data]);
  const shown = useMemo(() => folders.filter((f) => !f.hidden), [folders]);
  const picked = pickStore.usePicked();

  // ВЫБРАННОЙ ПАПКИ БОЛЬШЕ НЕТ (стёрли её тут же или на другом устройстве) — встаём на
  // первую: пустой список «по несуществующему отбору» человек не поймёт. Так же, если
  // папка «только с перепиской» опустела и ушла из полосы (П156 п.2).
  const current = shown.some((f) => f.id === picked) ? picked : (shown[0]?.id ?? '');

  const readDialog = useCallback(
    (folderId: string, was: number) => {
      if (!data) return;
      const next = foldersAfterRead(data.folders, folderId, was);
      if (next === data.folders) return; // считать нечего — лишней подменой не мигаем
      mutate({ ...data, folders: next });
    },
    [data, mutate],
  );

  return { folders, shown, current, setCurrent: pickStore.setPicked, reload, readDialog, ready: !!data };
}

// ── ВЕБ: полоса папок над списком ──────────────────────────────────────────────────

/**
 * ПОЛОСА ПАПОК В БРАУЗЕРЕ — ОДНА КАПСУЛА, ПРИБИТАЯ ПОД ШАПКОЙ РАЗДЕЛА (владелец
 * 10.09.2026, п.18: «как папки в Telegram iOS»). Разметка одна на «Чаты» и «Контакты»,
 * вид — в cabinet.css (`.msg-folders*`): стекло у капсулы, потому что она плавает над
 * содержимым — плашка поиска и список проезжают под ней (словарь облика: стекло только у
 * плавающего). Шестерёнка стоит первой В ЛЕНТЕ и своего кружка не носит; лента вместе с ней
 * прокручивается вбок внутри той же капсулы (ТЗ WEB-CAUSES М41).
 */
export function FoldersStrip({
  folders,
  current,
  onPick,
  onSettings,
  className,
  link: linkRef,
}: {
  /** Капсулы полосы: имя и число на наклейке (непрочитанные у «Чатов», люди у «Контактов»). */
  folders: { id: string; name: string; unread: number; account?: FolderAccount }[];
  current: string;
  onPick: (id: string) => void;
  /** Нет — полоса без шестерёнки (родной `MsgsFoldersBar.gearShown = false`: капсулы
   *  пропорций в редакторе гифки, кадры iOS M40/M41). */
  onSettings?: () => void;
  /** Место полосы у хозяина (прибита ли, поля) — класс хозяина; вид капсулы общий. */
  className?: string;
  /** Рука пейджера папок (`useFolderPager().strip`, ТЗ WEB-CAUSES М63): пилюля едет долей его хода. */
  link?: { current: StripDrive | null };
}) {
  const t = useTranslations('cabinet.messages.folders');
  const rowRef = useRef<HTMLDivElement | null>(null);
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const stripApi = useStripPill(rowRef, pillRef, current, folders);
  useLayoutEffect(() => {
    if (!linkRef) return undefined;
    linkRef.current = stripApi;
    return () => {
      if (linkRef.current === stripApi) linkRef.current = null;
    };
  }, [linkRef, stripApi]);
  return (
    <div className={className ? `msg-folders ${className}` : 'msg-folders'}>
      {/* INVARIANT(scriptera): шестерёнка — ПЕРВЫЙ элемент прокручиваемой ленты и уезжает вбок вместе
          с папками; соседом ленты (прибитой слева) её не ставить. Причина: владелец откатывал этот
          регресс много раз (память chats-folders-gear-scrolls, ЖЕЛЕЗНОЕ 11.09; ТЗ WEB-CAUSES М41,
          кадр 52def2e7). Канон: iOS MsgsFoldersBar (MsgsScreen.swift: «прибить её слева — регресс»). */}
      <div className="msg-folders__row" ref={rowRef}>
        {onSettings ? (
          <KBareButton className="msg-folders__gear" onClick={onSettings} aria-label={t('settings')} title={t('settings')}>
            <GearIcon />
          </KBareButton>
        ) : null}
        {/* Подложка выбранной папки — одна на ленту (М63), раму ставит useStripPill. */}
        <span className="msg-folders__pill" ref={pillRef} aria-hidden />
        {folders.map((f) => (
          <KBareButton
            key={f.id}
            data-fid={f.id}
            className={`msg-folder${f.id === current ? ' on' : ''}`}
            onClick={() => onPick(f.id)}
            aria-pressed={f.id === current}
            // ПАПКА-АККАУНТ СТОИТ КРУЖКАМИ (П65): слова в ней нет, и имя папки обязано
            // остаться голосу — иначе кнопка «без названия».
            {...(f.account ? { 'aria-label': f.name, title: f.name } : {})}
          >
            {f.account ? <FolderAccountFace name={f.name} account={f.account} /> : <span className="msg-folder__name">{f.name}</span>}
            {f.unread > 0 ? <span className="msg-folder__n">{f.unread}</span> : null}
          </KBareButton>
        ))}
      </div>
    </div>
  );
}

/** Рама подложки под словом в координатах ленты: слово + 12 в стороны, 2 сверху и снизу (М63;
 *  числа прежнего `inset: 2px -12px` у ::before). offset* — без поправок на transform предков. */
function pillBox(row: HTMLElement, id: string): { x: number; y: number; w: number; h: number } | null {
  const el = [...row.querySelectorAll<HTMLElement>('[data-fid]')].find((n) => n.dataset.fid === id);
  if (!el || el.offsetWidth === 0) return null;
  return { x: el.offsetLeft - 12, y: el.offsetTop + 2, w: el.offsetWidth + 24, h: el.offsetHeight - 4 };
}

const PILL_MOVE = 'transform var(--m-dur) var(--m-ease), width var(--m-dur) var(--m-ease), height var(--m-dur) var(--m-ease)';

/**
 * ПИЛЮЛЯ ПОЛОСЫ ПАПОК (ТЗ WEB-CAUSES М63; образец — iOS MsgsFoldersBar.setProgress, MsgsScreen.swift
 * 1790–1812): одна подложка, её рама — интерполяция между рамами слов `from` → `to` по доле хода
 * `p`; тон слов — смешение --mut → --ink той же долей. Ведёт её пейджер папок (свайп — каждым
 * кадром, тап и доводка — ходом --m-dur вместе со страницей); без пейджера (широкий экран, лист
 * пропорций) смена папки сама везёт пилюлю ходом.
 */
function useStripPill(
  rowRef: React.RefObject<HTMLDivElement | null>,
  pillRef: React.RefObject<HTMLSpanElement | null>,
  current: string,
  folders: readonly { id: string }[],
): StripDrive & { place: (id: string) => void } {
  const st = useRef({ cur: current, held: false, placed: false, stop: null as (() => void) | null });
  const api = useMemo<StripDrive & { place: (id: string) => void }>(() => {
    const words = (): HTMLElement[] => [...(rowRef.current?.querySelectorAll<HTMLElement>('[data-fid]') ?? [])];
    const paint = (from: string, to: string, p: number, move: boolean): void => {
      const row = rowRef.current;
      const pill = pillRef.current;
      if (!row || !pill) return;
      const a = pillBox(row, from) ?? pillBox(row, to);
      const b = pillBox(row, to) ?? a;
      if (!a || !b) {
        pill.style.visibility = 'hidden';
        return;
      }
      // В приложении полоса — родная; здесь (лист пропорций) — без хода, как было.
      const anim = move && st.current.placed && !document.documentElement.classList.contains('app');
      pill.style.visibility = '';
      pill.style.transition = anim ? PILL_MOVE : 'none';
      pill.style.transform = `translate(${a.x + (b.x - a.x) * p}px, ${a.y + (b.y - a.y) * p}px)`;
      pill.style.width = `${a.w + (b.w - a.w) * p}px`;
      pill.style.height = `${a.h + (b.h - a.h) * p}px`;
      st.current.placed = true;
    };
    /** ВЫБРАННОЕ СЛОВО — ЦЕЛИКОМ В КАПСУЛЕ (TARIFF-PAGE-TZ §11 П7, владелец 11.10.2026: «чип обрезан краем полосы»):
     *  лента докручивается так, чтобы слово с пилюлей (±12) было видно полностью — как iOS MsgsFoldersBar
     *  (scrollRectToVisible) и папки Telegram. Длинная лента на широком экране без этого оставляла выбранное
     *  слово за кромкой, а по обрезанной половине чипа клик не проходил. */
    const reveal = (id: string): void => {
      const row = rowRef.current;
      const box = row ? pillBox(row, id) : null;
      if (!row || !box) return;
      const left = box.x - 12;
      const right = box.x + box.w + 12;
      const view = row.clientWidth;
      if (row.scrollWidth <= view + 1) return;
      let target = row.scrollLeft;
      if (left < target) target = left;
      else if (right > target + view) target = right - view;
      if (Math.abs(target - row.scrollLeft) < 1) return;
      row.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    };
    /** Доводка к слову `to` ходом: тон слов — классом .on с переходом цвета (.is-moving). */
    const settle = (to: string): void => {
      const s = st.current;
      s.held = false;
      s.stop?.();
      const row = rowRef.current;
      reveal(to);
      if (row) {
        row.classList.add('is-moving');
        for (const w of words()) w.style.color = '';
        const timer = window.setTimeout(() => {
          row.classList.remove('is-moving');
          s.stop = null;
        }, 1000);
        s.stop = () => {
          window.clearTimeout(timer);
          row.classList.remove('is-moving');
          s.stop = null;
        };
      }
      paint(to, to, 0, true);
    };
    return {
      drag: (from, to, p) => {
        const s = st.current;
        s.held = true;
        s.stop?.();
        paint(from, to, p, false);
        for (const w of words()) {
          const share = (w.dataset.fid === from ? 1 - p : 0) + (w.dataset.fid === to ? p : 0);
          w.style.color = `color-mix(in srgb, var(--ink) ${Math.round(share * 1000) / 10}%, var(--mut))`;
        }
      },
      go: (to) => settle(to),
      back: () => settle(st.current.cur),
      /** Рама без хода (первая отрисовка, смена раскладки ленты). */
      place: (id) => {
        for (const w of words()) w.style.color = '';
        paint(id, id, 0, false);
        reveal(id);
      },
    };
  }, [rowRef, pillRef]);

  // Смена текущей папки и набора слов: пилюля к слову. Держит пейджер — его ход, не трогаем.
  const idsKey = folders.map((f) => f.id).join('\u0001');
  useLayoutEffect(() => {
    const s = st.current;
    const changed = s.cur !== current;
    s.cur = current;
    if (s.held) return;
    if (changed && s.placed) api.go(current);
    else if (!s.stop) api.place(current);
  }, [current, idsKey, api]);

  // КОЛЕСО МЫШИ НАД ЛЕНТОЙ ЛИСТАЕТ ЕЁ ВБОК (TARIFF-PAGE-TZ §11 П7): у ленты нет полосы прокрутки и стрелок, а
  // у обычной мыши — только вертикальное колесо; без этого папки за кромкой капсулы на широком экране были
  // недостижимы (трекпад листает сам — горизонтальный ход не трогаем). Слушатель не пассивный: страница
  // под лентой стоять не должна, пока лента ещё может ехать в ту сторону.
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const onWheel = (e: WheelEvent): void => {
      if (e.deltaX !== 0 || e.deltaY === 0 || e.ctrlKey) return;
      const room = row.scrollWidth - row.clientWidth;
      if (room <= 1) return;
      const next = Math.min(room, Math.max(0, row.scrollLeft + e.deltaY));
      if (next === row.scrollLeft) return;
      e.preventDefault();
      row.scrollLeft = next;
    };
    row.addEventListener('wheel', onWheel, { passive: false });
    return () => row.removeEventListener('wheel', onWheel);
  }, [rowRef]);

  // Раскладка ленты сменилась (шрифт, картинка аккаунта, полоса снова видна после поиска) —
  // рама заново, без хода.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const s = st.current;
      if (s.held || s.stop) return;
      api.place(s.cur);
    });
    ro.observe(row);
    for (const w of row.querySelectorAll('[data-fid]')) ro.observe(w);
    return () => ro.disconnect();
  }, [api, rowRef, idsKey]);

  return api;
}

/**
 * ПАПКА-АККАУНТ В ПОЛОСЕ — ДВА КРУЖКА ВМЕСТО СЛОВА (владелец 12.09.2026, П65: «в капсуле
 * два кружочка: значок соцсети и фотография»). Значок сети — 14 своим фирменным цветом,
 * фотография — 20 круглой картинкой НАШЕЙ ручки; фото нет или не загрузилось — буква
 * имени, тем же рецептом, что кружки записей (circleColor). Зеркало в приложении —
 * `MsgsFolderWord` по ключам `icon`/`iconColor`/`photo`.
 */
function FolderAccountFace({ name, account }: { name: string; account: FolderAccount }) {
  const [broken, setBroken] = useState(false);
  const chan = account.channel === 'ig' ? 'instagram' : 'telegram';
  const letter = (name.replace(/^@+/, '').trim() || name.trim() || '?').slice(0, 1).toUpperCase();
  return (
    <span className="msg-folder__acc" aria-hidden>
      <span className="msg-folder__ch" style={{ color: channelHex(chan) }}>
        <ChannelIcon channel={chan} size={14} />
      </span>
      {account.avatarUrl && !broken ? (
        // eslint-disable-next-line @next/next/no-img-element -- адрес нашей ручки, размер фиксированный
        <img className="msg-folder__ph" src={account.avatarUrl} alt="" width={20} height={20} decoding="async" loading="eager" onError={() => setBroken(true)} />
      ) : (
        // Буква — цветом СЕТИ, кеглем во всю плитку (кадры C01-chats-*: синяя «R» 20 у
        // Telegram-аккаунта без фото; MsgsFolderWord → ScrLetterTile(size: 20, color: iconColor)).
        <span className="msg-folder__ph is-letter" style={{ color: channelHex(chan) }}>
          {letter}
        </span>
      )}
    </span>
  );
}

/** Шестерёнка полосы — тот же контурный знак, что у строки свода «Настройки»; своего
    кружка и стекла у неё нет, она стоит прямо в капсуле полосы (владелец 10.09.2026). */
function GearIcon() {
  // Шестерёнка (gearshape iOS, кадры B01/C01), а не «солнце» — Б1/Ч3 ТЗ WEB-PARITY-3.
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ── Условия папки: словарь вариантов (ОДИН на веб и приложение) ────────────────────

type RuleGroupKey = 'kind' | 'auto' | 'channel' | 'window' | 'last';
/** Группы, у которых есть страница выбора (все «один из» плюс аккаунты и метки). */
type PickGroup = RuleGroupKey | 'accounts' | 'tags';

/** Варианты одного условия: значение правил ('' — условия нет) и ключ слова. */
const RULE_OPTIONS: Record<RuleGroupKey, { value: string; key: string }[]> = {
  // «Все» — это и есть «любые»: условий такая папка не ставит (folderWhere).
  kind: [
    { value: 'all', key: 'kindAll' },
    { value: 'direct', key: 'kindDirect' },
  ],
  auto: [
    { value: '', key: 'autoAny' },
    { value: 'on', key: 'autoOn' },
    { value: 'off', key: 'autoOff' },
  ],
  channel: [
    { value: '', key: 'chAny' },
    { value: 'ig', key: '' },
    { value: 'tg', key: '' },
  ],
  window: [
    { value: '', key: 'winAny' },
    { value: 'open', key: 'winOpen' },
    { value: 'closed', key: 'winClosed' },
  ],
  last: [
    { value: '', key: 'lastAny' },
    { value: 'mine', key: 'lastMine' },
    { value: 'theirs', key: 'lastTheirs' },
  ],
};

/** Имя канала — фирменное, не переводится и не сочиняется. */
const CHANNEL_WORD: Record<string, string> = { ig: 'Instagram', tg: 'Telegram' };

/** Канал переписки → полное имя площадки (им живут словари плиток и цветов сетей). */
const fullChannel = (channel: 'tg' | 'ig'): string => (channel === 'tg' ? 'telegram' : 'instagram');

/**
 * СВОДКА СТРОКИ «АККАУНТЫ» В РЕДАКТОРЕ ПАПКИ (владелец 12.09.2026, П66): один выбранный —
 * его имя, несколько — «N аккаунтов» (перечень из трёх логинов в строку всё равно не
 * влезал и уходил в многоточие), ни одного — «Неважно». Рука одна на веб-шторку и на
 * родные страницы — второй сводки у кабинета нет.
 */
function accountsWord(
  picked: readonly string[],
  accounts: readonly DialogAccount[],
  any: string,
  manyN: (n: number) => string,
  gone: string,
): string {
  // Правило одно и под тестом — lib/folder-accounts-word.ts (пакет Д2, доработка: «1 аккаунт»
  // вместо имени — ключ, которого нет в своде, теперь честно «Аккаунт отключён»).
  return folderAccountsWord(picked, accounts, { any, gone, many: manyN });
}

/** Что выбрано в группе условий: '' — условия нет. */
function rulePicked(rules: FolderRules, group: RuleGroupKey): string {
  if (group === 'kind') return rules.kind ?? 'all';
  return (rules[group] as string | undefined) ?? '';
}

/** Новые правила после выбора варианта: пустое значение снимает условие. */
function withRule(rules: FolderRules, group: RuleGroupKey, value: string): FolderRules {
  const next: FolderRules = { ...rules };
  if (!value) delete next[group];
  else (next as Record<string, unknown>)[group] = value;
  return next;
}

/** Метки человека — их предлагает редактор папки (общий свод, /api/cabinet/contact-tags). */
function useContactTags(): string[] {
  // Ключ — общий с «Контактами» (PREFETCH-TZ §2.5: дубль `contact-tags` снят): одна ручка — один кэш.
  const { data } = usePersistentData<{ ok: boolean; tags: { name: string }[] }>(
    'cabinet:contact-tags',
    '/api/cabinet/contact-tags',
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  return useMemo(() => (data?.tags ?? []).map((x) => x.name), [data]);
}

// ── Запись: одна рука на веб-шторку и родные страницы ───────────────────────────────

const apiCreate = api.create;
const apiPatch = api.patch;
const apiDelete = api.remove;
const apiReorder = api.reorder;
const apiRestore = api.restore;

// ── ВЕБ: шторка «Папки» ─────────────────────────────────────────────────────────────

/** Список папок веб-шторки: строки, «Добавить папку», «Восстановить папки по умолчанию». */
/** Значок папки героя «Папки» — ОДИН рисунок на папки ботов, чатов и контактов (кадры iOS
 *  B13-bots-folders-settings, C04-chats-folders, K15-contact-folders: SF `folder` героя —
 *  61 × 49 серым). Готового размера 66 × 52 (`set-hero-raw` — герой не растягивает его в
 *  квадрат и не перекрашивает); цвет — currentColor места (серый героя). */
export function FolderHeroGlyph() {
  return (
    <svg
      className="set-hero-raw"
      width={66}
      height={52}
      viewBox="0 0 66 52"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 44V9a6 6 0 0 1 6-6h13.5a5 5 0 0 1 3.6 1.5L30 9h27a6 6 0 0 1 6 6v29a6 6 0 0 1-6 6H9a6 6 0 0 1-6-6Z" />
      <path d="M3 16h60" />
    </svg>
  );
}

function FoldersList({ onEdit }: { onEdit: (id: string) => void }) {
  const t = useTranslations('cabinet.messages.folders');
  const tc = useTranslations('cabinet');
  const { folders, reload } = useDialogFolders();
  const phoneWeb = usePhoneLook();
  // РЕЖИМ ПРАВКИ СПИСКА (П85, владелец 13.09.2026): порядок — перетаскиванием за ручку,
  // удаление — красным кружком у строки (второй тап подтверждает). Кнопок «Выше/Ниже/
  // Удалить папку» в редакторе больше нет — ни на сайте, ни в приложении.
  return (
    // ОБЩИЙ список с «Изм.» (DEditList, кадр iOS C04-chats-folders): «Добавить папку» первой
    // строкой, у папки число условий справа и «›», «Изм.» — пилюля в шапке, порядок за «≡»,
    // удаление — минус → вопрос. Кнопок внизу нет. На телефоне над списком — герой: значок
    // папки и «Папки» по центру (Ч12), заголовка в шапке нет.
    <>
    {/* ГЕРОЙ — ИМЯ РАЗДЕЛА, ПОД НИМ МЕЛКО НАЗНАЧЕНИЕ (APP-CLAIMS-1020 ред. 11, Д6 / 10.9; слово владельца 21.10.2026:
        «в герое не «Папки», а «Чаты»; снизу мелко — «Настройка папок для страницы Чаты»»; ключ словаря тот же, что у
        страницы сервера folders.ts). */}
    {phoneWeb ? (
      <DHero icon={<FolderHeroGlyph />} color="var(--mut)" title={tc('sections.messages')}>
        {t('heroHint')}
      </DHero>
    ) : null}
    <DEditList
      addLabel={t('add')}
      onAdd={() => onEdit('')}
      removeLabel={t('deleteShort')}
      empty={t('empty')}
      onReorder={(ids) => void apiReorder(ids).then(reload)}
      items={folders.map((f) => ({
        key: f.id,
        label: f.name,
        value: t('condN', { n: folderRuleCount(f.rules) }),
        onOpen: () => onEdit(f.id),
        onRemove: () => {
          void askConfirm({
            question: t('deleteQ'),
            confirmLabel: t('deleteShort'),
            cancelLabel: tc('cancel'),
            destructive: true,
          }).then((yes) => {
            if (yes) void apiDelete(f.id).then(reload);
          });
        },
      }))}
    />
    </>
  );
}

/** Редактор одной папки: имя, условия, порядок, «Сохранить» и «Удалить папку». */
function FolderEditor({
  id,
  accounts,
  onDone,
}: {
  /** Пусто — новая папка. */
  id: string;
  accounts: DialogAccount[];
  onDone: () => void;
}) {
  const t = useTranslations('cabinet.messages.folders');
  const { folders, reload, setCurrent } = useDialogFolders();
  const tags = useContactTags();
  const folder = folders.find((f) => f.id === id) ?? null;
  const [name, setName] = useState(folder?.name ?? '');
  const [rules, setRules] = useState<FolderRules>(folder?.rules ?? {});
  const [busy, setBusy] = useState(false);
  // «ПОКАЗЫВАТЬ КАК АККАУНТ» (владелец 12.09.2026, П65): тумблер есть, только когда в
  // условиях выбран РОВНО ОДИН аккаунт, — тогда папка стоит в полосе парой кружков вместо
  // слова. Выбрали второй или сняли все — тумблера нет и метка снимается при сохранении.
  const [asAccount, setAsAccount] = useState(!!folder?.accountKey);
  const oneAccount = (rules.accounts ?? []).length === 1 ? (rules.accounts ?? [])[0]! : '';
  // СЧЁТЧИК (владелец 10.09.2026, п.14г): сколько переписок подходит под выбранное сейчас.
  const ta = useTranslations('cabinet.audience');
  const count = useFolderCount(FOLDERS_URL, rules);
  const countWords = count === null ? ta('counting') : t('matchN', { n: count });

  const panel = useSlidePanel();
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 Ч13/Ч14, кадр iOS C04-chats-folder-new): «Название» —
  // строкой «подпись · значение», условия — строки со значением справа, тумблеры в той же
  // карточке, «Подходят N переписок» серым под карточкой, сохранение — галочкой шапки.
  const phoneWeb = usePhoneLook();
  const saveRef = useRef<() => void>(() => {});
  const canSave = name.trim() !== '' && !busy;
  useEffect(() => {
    if (!phoneWeb) return;
    panel.update({ actionLabel: t('save'), onAction: () => saveRef.current() });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- рука читает свежее через ref
  }, [phoneWeb]);
  const rulesRef = useRef(rules);
  rulesRef.current = rules;
  const optWord = (group: RuleGroupKey, value: string): string => {
    const o = RULE_OPTIONS[group].find((x) => x.value === value);
    return o ? (o.key ? t(o.key) : (CHANNEL_WORD[o.value] ?? o.value)) : '';
  };
  const pickedWord = (group: PickGroup): string => {
    if (group === 'accounts') {
      return accountsWord(rules.accounts ?? [], accounts, t('any'), (n) => t('accountsN', { n }), t('accountGone'));
    }
    if (group === 'tags') return (rules.tags ?? []).length > 0 ? (rules.tags ?? []).join(', ') : t('any');
    return optWord(group, rulePicked(rules, group));
  };
  const groupLabel = (group: PickGroup): string =>
    ({ kind: t('grpKind'), auto: t('grpAuto'), accounts: t('grpAccounts'), channel: t('grpChannel'), window: t('grpWindow'), last: t('grpLast'), tags: t('grpTags') })[group];
  /** Страница выбора условия — тот же список галочками, что у листа условий рассылки. */
  const openPick = (group: PickGroup): void => {
    const multi = group === 'accounts' || group === 'tags';
    const options =
      group === 'accounts'
        ? accounts.map((a) => ({
            value: a.key,
            label: a.name || t(a.channel === 'ig' ? 'accountNoNameIg' : 'accountNoNameTg'),
            // ОТКЛЮЧЁННЫЙ АККАУНТ ОСТАЁТСЯ В СПИСКЕ (владелец 13.09.2026, П90: «переписки
            // убирать не надо, всё показывать») — со сноской, выбирается как любой.
            // ТЕЛЕФОН В БРАУЗЕРЕ (кадр iOS C04-folder-cond-accounts): сеть — плиткой со знаком
            // слева, а не подстрокой «Telegram/Instagram»; сноска остаётся только у отключённого.
            ...(phoneWeb
              ? {
                  icon: (
                    <span className="k-lrow__ico" style={{ color: channelHex(a.channel === 'ig' ? 'instagram' : 'telegram') }} aria-hidden>
                      <ChannelIcon channel={a.channel === 'ig' ? 'instagram' : 'telegram'} size={18} />
                    </span>
                  ),
                  ...(a.off ? { hint: t('accountOff') } : {}),
                }
              : { hint: a.off ? t('accountOff') : a.channel === 'ig' ? 'Instagram' : 'Telegram' }),
          }))
        : group === 'tags'
          ? tags.map((x) => ({ value: x, label: x }))
          : RULE_OPTIONS[group].map((o) => ({ value: o.value || 'any', label: o.key ? t(o.key) : (CHANNEL_WORD[o.value] ?? o.value) }));
    const chosen = multi
      ? ((group === 'accounts' ? rulesRef.current.accounts : rulesRef.current.tags) ?? [])
      : [rulePicked(rulesRef.current, group) || 'any'];
    panel.open({
      title: groupLabel(group),
      sheet: true,
      // Лист выбора условия — ПОЛУЛИСТ с ручкой над редактором, как родной (`present: 'half'` ниже;
      // кадр iOS C04-folder-cond-messages, сверка 03.10): редактор под ним остаётся на месте.
      // Слева «×», а не «‹» (кадры C04-folder-cond-*: родной полулист закрывается крестиком) —
      // тот же лист-стопка `deck`, что у выбора ботов папки (bots-folders.tsx).
      ...(phoneWeb ? { sheetDetent: 0.5 } : {}),
      winKey: `fld-pick-${group}`,
      body: (
        <PickPage
          options={options}
          chosen={chosen}
          title={groupLabel(group)}
          // Карточка — в 31 под центром «×» (кадры C04-folder-cond-*: у веба было 62).
          {...(phoneWeb ? { bare: true, flush: true } : {})}
          hint={t('pickHint')}
          empty={t('any')}
          single={!multi}
          onPick={(values) =>
            setRules((r) => {
              if (group === 'accounts' || group === 'tags') {
                const next = { ...r };
                if (values.length > 0) next[group] = values;
                else delete next[group];
                return next;
              }
              const v = values[0] ?? 'any';
              return withRule(r, group, v === 'any' ? '' : v);
            })
          }
        />
      ),
    });
  };

  const save = async () => {
    const clean = name.trim();
    if (!clean || busy) return;
    setBusy(true);
    try {
      const accountKey = asAccount && oneAccount ? oneAccount : null;
      if (folder) await apiPatch(folder.id, { name: clean, rules, accountKey });
      else {
        const made = await apiCreate(clean, rules, accountKey);
        if (made) setCurrent(made.id);
      }
      reload();
      onDone();
    } finally {
      setBusy(false);
    }
  };

  saveRef.current = () => {
    if (canSave) void save();
  };

  if (phoneWeb) {
    const condRows = [
      { key: 'kind', label: t('grpKind'), value: pickedWord('kind'), onOpen: () => openPick('kind') },
      { key: 'auto', label: t('grpAuto'), value: pickedWord('auto'), onOpen: () => openPick('auto') },
      ...(accounts.length > 0 ? [{ key: 'accounts', label: t('grpAccounts'), value: pickedWord('accounts'), onOpen: () => openPick('accounts') }] : []),
      { key: 'channel', label: t('grpChannel'), value: pickedWord('channel'), onOpen: () => openPick('channel') },
      { key: 'window', label: t('grpWindow'), value: pickedWord('window'), onOpen: () => openPick('window') },
      { key: 'last', label: t('grpLast'), value: pickedWord('last'), onOpen: () => openPick('last') },
      ...(tags.length > 0 ? [{ key: 'tags', label: t('grpTags'), value: pickedWord('tags'), onOpen: () => openPick('tags') }] : []),
    ];
    const setFlag = (key: 'unread' | 'nonEmpty', on: boolean) =>
      setRules((r) => {
        const next = { ...r };
        if (on) next[key] = true;
        else delete next[key];
        return next;
      });
    return (
      <>
        <FBlock label={t('name')} className="fld-name-blk">
          <KListGroup>
            <KListRow
              className="fld-name-row"
              title={t('name')}
              control
              value={<KInput value={name} placeholder={t('namePh')} aria-label={t('name')} maxLength={30} onChange={(e) => setName(e.target.value)} />}
            />
          </KListGroup>
        </FBlock>
        <FBlock label={null} className="fld-conds">
          <KListGroup>
            {condRows.map((r) => (
              <KListRow key={r.key} title={r.label} value={r.value} chevron onClick={r.onOpen} />
            ))}
            {oneAccount ? (
              <KListRow title={t('asAccount')} control value={<KSwitch ariaLabel={t('asAccount')} checked={asAccount} onChange={setAsAccount} />} />
            ) : null}
            <KListRow title={t('unreadOnly')} control value={<KSwitch ariaLabel={t('unreadOnly')} checked={!!rules.unread} onChange={(on) => setFlag('unread', on)} />} />
            <KListRow title={t('nonEmptyOnly')} control value={<KSwitch ariaLabel={t('nonEmptyOnly')} checked={!!rules.nonEmpty} onChange={(on) => setFlag('nonEmpty', on)} />} />
          </KListGroup>
        </FBlock>
        <FHint className="fld-count">{countWords}</FHint>
      </>
    );
  }

  return (
    <>
      <FBlock label={t('name')} hint={t('rulesHint')}>
        <FIn label={t('name')}>
          <KInput value={name} placeholder={t('namePh')} maxLength={30} onChange={(e) => setName(e.target.value)} />
        </FIn>
      </FBlock>
      {/* СТРОКИ «ПУНКТ → ЗНАЧЕНИЕ → ШЕВРОН» (владелец 10.09.2026): вид и механика — как у
          листа условий рассылки, условия папок «Чатов» свои. Каждая строка открывает
          страницу выбора; «Непрочитанные» — галочка на месте. */}
      <FNotifyList
        hint={countWords}
        items={[
          { key: 'kind', label: t('grpKind'), summary: pickedWord('kind'), onOpen: () => openPick('kind') },
          { key: 'auto', label: t('grpAuto'), summary: pickedWord('auto'), onOpen: () => openPick('auto') },
          ...(accounts.length > 0
            ? [{ key: 'accounts', label: t('grpAccounts'), summary: pickedWord('accounts'), onOpen: () => openPick('accounts') }]
            : []),
          { key: 'channel', label: t('grpChannel'), summary: pickedWord('channel'), onOpen: () => openPick('channel') },
          { key: 'window', label: t('grpWindow'), summary: pickedWord('window'), onOpen: () => openPick('window') },
          { key: 'last', label: t('grpLast'), summary: pickedWord('last'), onOpen: () => openPick('last') },
          ...(tags.length > 0
            ? [{ key: 'tags', label: t('grpTags'), summary: pickedWord('tags'), onOpen: () => openPick('tags') }]
            : []),
        ]}
      />
      {oneAccount ? (
        <FChoiceManyBlock
          label={t('grpAccounts')}
          values={asAccount ? ['acc'] : []}
          options={[{ value: 'acc', label: t('asAccount') }]}
          onChange={(v) => setAsAccount(v.includes('acc'))}
        />
      ) : null}
      <FChoiceManyBlock
        label={t('grpUnread')}
        values={[...(rules.unread ? ['unread'] : []), ...(rules.nonEmpty ? ['nonEmpty'] : [])]}
        // «Только с перепиской» — строкой под «Только непрочитанные» (П156 п.2).
        options={[
          { value: 'unread', label: t('unreadOnly') },
          { value: 'nonEmpty', label: t('nonEmptyOnly') },
        ]}
        onChange={(v) =>
          setRules((r) => {
            const next = { ...r };
            if (v.includes('unread')) next.unread = true;
            else delete next.unread;
            if (v.includes('nonEmpty')) next.nonEmpty = true;
            else delete next.nonEmpty;
            return next;
          })
        }
      />
      <KActionBar>
        <KButton tone="primary" disabled={!name.trim() || busy} onClick={() => void save()}>
          {t('save')}
        </KButton>
      </KActionBar>
    </>
  );
}

// ── ПРИЛОЖЕНИЕ: те же настройки родными страницами ─────────────────────────────────

/** Ключи строк родных страниц: по ним разбираются msgsFoldersTap/Set. */
const ROW = {
  name: 'fld:name',
  /** «Показывать как аккаунт» (П65). */
  asAcc: 'fld:asacc',
  /** ✓ мультиселект-полушторки (П92): закрыть страницу выбора. */
  pickDone: 'fld:pick:done',
  add: 'fld:new',
  edit: 'fld:edit',
  save: 'fld:save',
  restore: 'fld:restore',
  /** Удаление — свайпом строки списка (П85): ключ с id папки в хвосте. */
  del: 'fld:del:',
  /** Новый порядок папок — `accountSet` из перетаскивания (П85), id через запятую. */
  order: 'fld:order',
} as const;

/** ИМЕНА МОСТА полушторки «Папки» — константа модуля (общий водитель держит их слепок). */
export const FOLDERS_NAMES: NativePagesNames = {
  open: 'msgsFoldersSheet',
  patch: 'msgsFoldersPatch',
  close: 'msgsFoldersClose',
  tap: 'msgsFoldersTap',
  set: 'msgsFoldersSet',
  back: 'msgsFoldersBack',
};

/** Сборка умеет родную полушторку настроек папок. */
export function nativeFoldersSupported(): boolean {
  return nativeMethod('msgsFoldersSheet');
}

/**
 * НАСТРОЙКИ ПАПОК — ОДНА РУКА НА ОБА МИРА. В приложении со свежей сборкой открывается
 * родная полушторка (страницы собирает веб теми же моделями, что «Аккаунт»); в браузере
 * и на старых сборках — веб-шторка form-kit тем же составом (feature-detect, ТЗ M.5 п.5).
 */
/** Ключи трёх страниц папок с префиксом хозяина: у листа «Чатов» префикс пуст, у стопки
 *  «Настроек» — `page:fld:` (П107). */
function folderPageKeys(prefix: string) {
  return { list: `${prefix}folders`, edit: `${prefix}folder`, pick: `${prefix}folder-pick` } as const;
}

/**
 * ОБЩИЙ СТРОИТЕЛЬ СТРАНИЦ ПАПОК «ЧАТОВ» (П107, владелец 13.09.2026: «Папки → Чаты» из
 * настроек открывали чужую страницу). Список, папка и выбор условия собираются ЗДЕСЬ один
 * раз; хозяев два — лист раздела «Чаты» (`useFoldersSettings`, свой `msgsFoldersSheet`) и
 * стопка «Настроек» (`useNativeChatFoldersPages`). Стопку ведёт хозяин (`nav`): ключи
 * страниц у каждого свои, а тапы (`fld:*`) — одни и те же.
 */
function useFolderPagesCore(
  accounts: DialogAccount[],
  nav: { prefix: string; stack: string[]; setStack: (next: string[]) => void },
): {
  pages: NativeAccountPage[];
  applyTap: (key: string) => void;
  applySet: (key: string, value: string | boolean) => void;
  back: (key: string) => void;
  openList: () => void;
  openWebEditor: (id: string) => void;
} {
  const K = folderPageKeys(nav.prefix);
  const { stack, setStack } = nav;
  const t = useTranslations('cabinet.messages.folders');
  const tsec = useTranslations('cabinet.sections');
  const tc = useTranslations('cabinet');
  const ta = useTranslations('cabinet.audience');
  const panel = useSlidePanel();
  const { folders, reload, setCurrent } = useDialogFolders();
  const tags = useContactTags();
  // ТЕКСТОВАЯ КНОПКА В ШАПКЕ («Править»/«Готово») умеет только свежая сборка (признак
  // способности `accountTopAction`, CLAUDE.md запрет №8). Старая держит прежний ход:
  // «Добавить папку» и «Восстановить…» стоят в пузыре всегда, «Сохранить» — кнопкой внизу.
  const topNative = nativeMethod('accountTopAction');

  /** Какую папку правим ('' — новая, ещё не созданная). */
  const [editing, setEditing] = useState<string>('');
  /** РЕЖИМ ПРАВКИ СПИСКА (владелец 10.09.2026, п.14б): вне него — только список папок;
   *  «Править» справа в шапке открывает добавление и восстановление. */
  const [editMode, setEditMode] = useState(false);
  /** ЧЕРНОВИК РЕДАКТОРА (владелец 10.09.2026, п.14д): имя и условия правятся на месте и
   *  уходят на сервер ОДНИМ ходом по «Готово» — прежнее «папка заводится по первому вводу
   *  имени и дальше сама сохраняется» человек не понял. «Назад» черновик бросает. */
  const [draft, setDraft] = useState<{ name: string; rules: FolderRules; asAccount: boolean } | null>(null);
  /** Какая группа условий открыта страницей выбора. */
  const [pickGroup, setPickGroup] = useState<PickGroup>('kind');
  const folder = folders.find((f) => f.id === editing) ?? null;
  const rules: FolderRules = draft?.rules ?? {};
  // «ПОКАЗЫВАТЬ КАК АККАУНТ» (владелец 12.09.2026, П65): строка есть, только когда в
  // условиях выбран РОВНО ОДИН аккаунт; иначе метка снимается при сохранении.
  const oneAccount = (rules.accounts ?? []).length === 1 ? (rules.accounts ?? [])[0]! : '';
  // Аккаунт превью (П91) и его фото base64 тем же конвейером, что лица родных экранов.
  const previewSrc = draft?.asAccount && oneAccount ? (accounts.find((a) => a.key === oneAccount) ?? null) : null;
  const { photos: previewPhotos } = useNativePhotos(previewSrc?.avatarUrl ? [previewSrc.avatarUrl] : [], 40);
  const previewAccount = previewSrc
    ? {
        icon: nativeChannelTile(fullChannel(previewSrc.channel)).icon,
        iconColor: channelHex(fullChannel(previewSrc.channel)),
        photo: previewSrc.avatarUrl ? (previewPhotos[previewSrc.avatarUrl] ?? '') : '',
        text: shortAccountName(previewSrc.name),
      }
    : null;
  const inEditor = stack.includes('folder');
  // СЧЁТЧИК (п.14г): сколько переписок подходит под условия черновика — пересчёт на
  // каждый выбор, как «Получит N» у рассылки.
  const count = useFolderCount(FOLDERS_URL, inEditor ? rules : null);
  const countWords = count === null ? ta('counting') : t('matchN', { n: count });

  const condWord = (f: DialogFolder) => t('condN', { n: folderRuleCount(f.rules) });
  /** Слово варианта группы (каналы — фирменные имена, не переводятся). */
  const optWord = (group: RuleGroupKey, value: string): string => {
    const o = RULE_OPTIONS[group].find((x) => x.value === value);
    return o ? (o.key ? t(o.key) : (CHANNEL_WORD[o.value] ?? o.value)) : '';
  };
  /** Что выбрано в группе — словами для строки редактора. */
  const pickedWord = (group: PickGroup): string => {
    if (group === 'accounts') {
      return accountsWord(rules.accounts ?? [], accounts, t('any'), (n) => t('accountsN', { n }), t('accountGone'));
    }
    if (group === 'tags') return (rules.tags ?? []).length > 0 ? (rules.tags ?? []).join(', ') : t('any');
    return optWord(group, rulePicked(rules, group));
  };
  const navRow = (group: PickGroup, label: string): NativeAccountField => ({
    key: `fld:g:${group}`,
    kind: 'nav',
    title: label,
    value: pickedWord(group),
    page: 'folder-pick',
  });
  const groupLabel = (group: PickGroup): string =>
    group === 'kind'
      ? t('grpKind')
      : group === 'auto'
        ? t('grpAuto')
        : group === 'accounts'
          ? t('grpAccounts')
          : group === 'channel'
            ? t('grpChannel')
            : group === 'window'
              ? t('grpWindow')
              : group === 'last'
                ? t('grpLast')
                : t('grpTags');

  // СПИСОК ПАПОК ПО КАНОНУ APPLE (владелец 10.09.2026, п.14б): вне правки — один список,
  // справа в шапке «Править»; в правке — «Готово». Сборка без текстовой кнопки — прежний
  // вид (обе кнопки в пузыре всегда).
  //
  // «ДОБАВИТЬ ПАПКУ» СТОИТ В ПУЗЫРЕ ВСЕГДА (владелец 12.09.2026, П60): завести папку —
  // обычное дело, прятать его за «Править» незачем.
  const listPage: NativeAccountPage = {
    key: K.list,
    title: t('title'),
    // ПОД ЗАГОЛОВКОМ — ОДНО СЛОВО РАЗДЕЛА СЕРЫМ (владелец 14.09.2026), одинаково у
    // «Ботов», «Чатов» и «Контактов».
    subtitle: tsec('messages'),
    // Список папок — сразу на весь экран стопкой, с героем; правка ложится поверх
    // маленькой полушторкой (решение владельца 14.09.2026). Одинаково во всех разделах.
    present: 'deck',
    // Под заголовком героя — целое предложение (владелец 14.09.2026).
    hero: { icon: 'folder', color: accountRoleHex('var(--accent-stroke)'), title: t('title'), meta: t('lineHint') },
    ...(topNative ? { topAction: { key: ROW.edit, label: editMode ? tc('done') : t('edit') } } : {}),
    groups: [
      {
        key: 'list',
        // Описание живёт под заголовком героя; над пузырём ничего не пишем.
        ...(folders.length === 0 ? { hint: t('empty') } : {}),
        // ПОРЯДОК — ПЕРЕТАСКИВАНИЕМ В РЕЖИМЕ «ПРАВИТЬ» (П85, владелец 13.09.2026): у
        // строк ручка справа, новый порядок приходит `accountSet` ключом `order`.
        // ПЕРЕТАСКИВАНИЕ ПАПОК ВЕРНУЛОСЬ (владелец 14.09.2026, вторая правка того же дня).
        ...(editMode ? { reorder: ROW.order } : {}),
        // «Создать папку» — первой строкой пузыря (эталон Telegram, владелец 14.09.2026).
        fields: [
          { key: ROW.add, kind: 'button' as const, title: t('add'), tone: 'accent' as const, page: K.edit },
          ...folders.map(
            (f): NativeAccountField => ({
              key: `fld:${f.id}`,
              kind: 'nav',
              title: f.name,
              value: condWord(f),
              page: K.edit,
              // Удаление — свайпом строки влево (П85): красная плашка «Удалить», веб
              // спрашивает подтверждение и удаляет. Работает и вне режима правки.
              dragId: f.id,
              swipeDelete: `${ROW.del}${f.id}`,
              swipeLabel: t('deleteShort'),
              ...(editMode ? { mark: 'line.3.horizontal' } : {}),
            }),
          ),
        ],
      },
      // КНОПКИ «ВОССТАНОВИТЬ ПАПКИ ПО УМОЛЧАНИЮ» НЕТ (владелец 14.09.2026: «восстановить
      // папки по умолчанию — не надо для этого кнопку делать»).
    ],
  };

  const editPage: NativeAccountPage = {
    key: K.edit,
    // ЗАГОЛОВОК — «Добавить папку» у новой, имя у существующей; под ним серым слово
    // раздела (владелец 14.09.2026). Одинаково у «Ботов», «Чатов» и «Контактов».
    title: folder?.name || t('add'),
    subtitle: tsec('messages'),
    // У «ЧАТОВ» И «КОНТАКТОВ» РЕДАКТОР ОТКРЫВАЕТСЯ СРАЗУ ПОЛНОСТЬЮ (решение владельца
    // 14.09.2026): условий у папки много, полушторки им мало. Полушторка осталась только
    // у «Ботов», где на странице лишь имя и список ботов.
    present: 'deck',
    // ГАЛОЧКА ВМЕСТО СЛОВА «Готово» (владелец 14.09.2026) — как у «Ботов».
    ...(topNative ? { topAction: { key: ROW.save, label: tc('done'), icon: 'checkmark' as const } } : {}),
    groups: [
      {
        key: 'name',
        label: t('name'),
        fields: [
          // ПАПКА-АККАУНТ — ПРЕВЬЮ ВМЕСТО ПОЛЯ (владелец 13.09.2026, П91): тумблер включён и в
          // условиях ровно один аккаунт — имя не спрашиваем, показываем, как папка встанет
          // в полосу (значок сети + фото + короткое имя); имя папки при сохранении = короткое
          // имя аккаунта. Выключили — снова поле с прежним набранным текстом.
          previewAccount
            ? {
                key: ROW.name,
                kind: 'nav' as const,
                title: '',
                value: previewAccount.text,
                disabled: true,
                preview: previewAccount,
              }
            : {
                key: ROW.name,
                kind: 'field' as const,
                title: t('name'),
                value: draft?.name ?? '',
                placeholder: t('namePh'),
                max: 30,
              },
        ],
      },
      {
        key: 'fld:rows',
        // Счётчик — сноской под пузырём условий: считается по черновику на каждый выбор.
        hint: countWords,
        fields: [
          navRow('kind', t('grpKind')),
          navRow('auto', t('grpAuto')),
          ...(accounts.length > 0 ? [navRow('accounts', t('grpAccounts'))] : []),
          navRow('channel', t('grpChannel')),
          navRow('window', t('grpWindow')),
          navRow('last', t('grpLast')),
          ...(tags.length > 0 ? [navRow('tags', t('grpTags'))] : []),
          // Тумблер — ПОСЛЕДНИМ в пузыре, после строк-переходов (владелец 10.09.2026, п.14е).
          { key: 'fld:unread:on', kind: 'switch', title: t('unreadOnly'), on: !!rules.unread },
          // «Только с перепиской» — строкой под ним (П156 п.2): пустая папка не стоит в полосе.
          { key: 'fld:nonempty:on', kind: 'switch', title: t('nonEmptyOnly'), on: !!rules.nonEmpty },
          ...(oneAccount
            ? [{ key: ROW.asAcc, kind: 'switch' as const, title: t('asAccount'), on: !!draft?.asAccount }]
            : []),
        ],
      },
      ...(topNative
        ? []
        : [{ key: 'fld:save', fields: [{ key: ROW.save, kind: 'button' as const, title: t('save'), tone: 'accent' as const }] }]),
    ],
  };

  /** СТРАНИЦА ВЫБОРА ОДНОГО УСЛОВИЯ (`folder-pick`): варианты галочками — одна у групп
   *  «один из», несколько у аккаунтов и меток. Какая группа открыта — `pickGroup`.
   *  ПОЛУШТОРКОЙ (владелец 10.09.2026, п.14в): выбор короткий, под листом остаётся
   *  редактор; вытянул на весь экран — лист ложится стопкой. */
  const pickPage: NativeAccountPage = {
    key: K.pick,
    title: groupLabel(pickGroup),
    present: 'half',
    // МУЛЬТИСЕЛЕКТ (аккаунты, метки) закрывается галочкой справа сверху (П92); у одиночного
    // выбора кнопки нет — лист уходит сам после тапа. Старая сборка без `icon` видит слово.
    ...(topNative && (pickGroup === 'accounts' || pickGroup === 'tags')
      ? { topAction: { key: ROW.pickDone, label: tc('done'), icon: 'checkmark' } }
      : {}),
    groups: [
      {
        key: 'pick',
        hint: t('pickHint'),
        fields:
          pickGroup === 'accounts'
            ? accounts.map(
                (a): NativeAccountField => ({
                  key: `fld:acc:${a.key}`,
                  kind: 'check',
                  // Аккаунт без имени (П90, 13.09.2026): подпись сетью, а не пустая строка.
                  title: a.name || t(a.channel === 'ig' ? 'accountNoNameIg' : 'accountNoNameTg'),
                  // Отключённый аккаунт (П90, владелец 13.09.2026): строка остаётся, под ней
                  // сноска «Аккаунт отключён, переписки остались»; выбирается как любой.
                  ...(a.off ? { hint: t('accountOff') } : {}),
                  // ЗНАЧОК СЕТИ — НАШЕЙ ПЛИТКОЙ (владелец 12.09.2026, П66): пастельная
                  // подложка цвета сети со значком каталога, как на карточках ботов, а не
                  // фирменная картинка с градиентом. Плитку даёт ОДИН словарь на всё
                  // приложение (nativeChannelTile/channelHex) — второй таблицы цветов и
                  // имён у сетей нет. Прежнего `image` здесь быть не должно: у родной
                  // строки картинка побеждает плитку.
                  icon: nativeChannelTile(fullChannel(a.channel)).icon,
                  color: channelHex(fullChannel(a.channel)),
                  on: (rules.accounts ?? []).includes(a.key),
                }),
              )
            : pickGroup === 'tags'
              ? tags.map(
                  (x): NativeAccountField => ({ key: `fld:tag:${x}`, kind: 'check', title: x, on: (rules.tags ?? []).includes(x) }),
                )
              : RULE_OPTIONS[pickGroup].map(
                  (o): NativeAccountField => ({
                    key: `fld:${pickGroup}:${o.value || 'any'}`,
                    kind: 'check',
                    radio: true,
                    title: o.key ? t(o.key) : (CHANNEL_WORD[o.value] ?? o.value),
                    on: rulePicked(rules, pickGroup) === o.value,
                  }),
                ),
      },
    ],
  };


  /** Правка условий — ТОЛЬКО в черновик; на сервер всё уходит по «Готово» (п.14д). */
  const setRules = (next: FolderRules) => {
    setDraft((d) => ({ name: d?.name ?? '', rules: next, asAccount: !!d?.asAccount }));
  };

  const applySet = (key: string, value: string | boolean): void => {
    if (key === ROW.order && typeof value === 'string') {
      // Новый порядок из перетаскивания (П85): id через запятую, как отдал натив.
      void apiReorder(value.split(',').filter(Boolean)).then(reload);
      return;
    }
    if (key === ROW.name && typeof value === 'string') {
      setDraft((d) => ({ name: value, rules: d?.rules ?? {}, asAccount: !!d?.asAccount }));
      return;
    }
    if (key === ROW.asAcc && typeof value === 'boolean') {
      setDraft((d) => ({ name: d?.name ?? '', rules: d?.rules ?? {}, asAccount: value }));
      return;
    }
    if (key === 'fld:unread:on' && typeof value === 'boolean') {
      const next = { ...rules };
      if (value) next.unread = true;
      else delete next.unread;
      setRules(next);
      return;
    }
    if (key === 'fld:nonempty:on' && typeof value === 'boolean') {
      const next = { ...rules };
      if (value) next.nonEmpty = true;
      else delete next.nonEmpty;
      setRules(next);
      return;
    }
    const acc = key.startsWith('fld:acc:') ? key.slice('fld:acc:'.length) : '';
    if (acc && typeof value === 'boolean') {
      const have = new Set(rules.accounts ?? []);
      if (value) have.add(acc);
      else have.delete(acc);
      const next = { ...rules };
      if (have.size > 0) next.accounts = [...have];
      else delete next.accounts;
      setRules(next);
      return;
    }
    const tag = key.startsWith('fld:tag:') ? key.slice('fld:tag:'.length) : '';
    if (tag && typeof value === 'boolean') {
      const have = new Set(rules.tags ?? []);
      if (value) have.add(tag);
      else have.delete(tag);
      const next = { ...rules };
      if (have.size > 0) next.tags = [...have];
      else delete next.tags;
      setRules(next);
      return;
    }
    const m = /^fld:(kind|auto|channel|window|last):(.+)$/.exec(key);
    if (m && value === true) {
      const group = m[1] as RuleGroupKey;
      const picked = m[2] === 'any' ? '' : m[2]!;
      setRules(withRule(rules, group, picked));
      // ОДИНОЧНЫЙ ВЫБОР ЗАКРЫВАЕТ ПОЛУШТОРКУ САМ (владелец 13.09.2026, П92): выбрали —
      // лист уезжает, под ним редактор с новым значением.
      setStack([K.list, K.edit]);
    }
  };

  /** «Готово» редактора: одна запись — новая папка или правка имени и условий разом. */
  const saveDraft = (): void => {
    const clean = previewAccount ? previewAccount.text : (draft?.name ?? '').trim();
    if (!clean) return;
    const done = () => {
      reload();
      setDraft(null);
      setStack([K.list]);
    };
    const accountKey = draft?.asAccount && oneAccount ? oneAccount : null;
    if (folder) void apiPatch(folder.id, { name: clean, rules, accountKey }).then(done);
    else
      void apiCreate(clean, rules, accountKey).then((made) => {
        if (made) setCurrent(made.id);
        done();
      });
  };

  const openEditor = (f: DialogFolder | null): void => {
    setEditing(f?.id ?? '');
    setDraft({ name: f?.name ?? '', rules: f?.rules ?? {}, asAccount: !!f?.accountKey });
    setStack([K.list, K.edit]);
  };

  const applyTap = (key: string): void => {
    const g = /^fld:g:(kind|auto|accounts|channel|window|last|tags)$/.exec(key);
    if (g) {
      setPickGroup(g[1] as PickGroup);
      setStack([K.list, K.edit, K.pick]);
      return;
    }
    if (key === ROW.edit) {
      setEditMode((v) => !v);
      return;
    }
    if (key === ROW.save) {
      saveDraft();
      return;
    }
    if (key === ROW.pickDone) {
      setStack([K.list, K.edit]);
      return;
    }
    if (key === ROW.restore) {
      // ПРЕДУПРЕЖДЕНИЕ ПЕРЕД ВОССТАНОВЛЕНИЕМ (владелец 10.09.2026, п.14а): что именно
      // вернётся и что свои папки останутся, — родным подтверждением.
      const done = () => void apiRestore().then(reload);
      // Окно-вопрос — одно на приложение и браузер (ask.tsx): раньше браузер восстанавливал молча.
      void askConfirm({
        question: t('restoreQ'),
        confirmLabel: t('restoreShort'),
        cancelLabel: tc('cancel'),
        destructive: false,
      }).then((yes) => {
        if (yes) done();
      });
      return;
    }
    if (key === ROW.add) {
      openEditor(null);
      return;
    }
    if (key.startsWith(ROW.del)) {
      // Свайп строки списка → красная плашка → сюда (П85): подтверждение — родное.
      const id = key.slice(ROW.del.length);
      const done = () => {
        void apiDelete(id).then(() => {
          reload();
          if (editing === id) {
            setDraft(null);
            setStack([K.list]);
          }
        });
      };
      // Вопрос перед удалением — и в браузере тоже (ТЗ WEB-PARITY-3 6.0 п.2, кадр iOS
      // C04-chats-folder-delete-ask; раньше веб убирал папку без вопроса — FINDINGS-2 п.17).
      void askConfirm({
        question: t('deleteQ'),
        confirmLabel: t('deleteShort'),
        cancelLabel: tc('cancel'),
        destructive: true,
      }).then((yes) => {
        if (yes) done();
      });
      return;
    }
    if (key.startsWith('fld:')) {
      const f = folders.find((x) => x.id === key.slice(4));
      if (f) openEditor(f);
    }
  };

  const back = (key: string): void => {
    if (key === K.pick) return setStack([K.list, K.edit]);
    // «Назад» из редактора — черновик брошен, на сервере ничего не менялось.
    setDraft(null);
    if (key === K.edit) return setStack([K.list]);
    // ПОЛУШТОРКУ ЗАКРЫЛИ ЦЕЛИКОМ — РЕЖИМ ПРАВКИ ГАСНЕТ (владелец 12.09.2026, П61):
    // крестик, свайп и «Готово» приходят сюда одним и тем же ключом корневой страницы
    // («Утянули вниз» натив тоже адресует корню), поэтому место одно. Следующее
    // открытие начинается обычным списком с «Править» — его же ставит `openList`.
    setEditMode(false);
    setStack([]);
  };

  const openWebEditor = useCallback(
    (id: string) => {
      // ТЕЛЕФОН ИЛИ ПАНЕЛЬ-ТЕЛЕФОН 460 (пакет Д2.2 п.9, Закон 3: правило по контейнеру): открыта из
      // листа «Папки» на широком экране — тоже уровень стопки со «‹», а не подмена с «×».
      const phone = readPhoneLook();
      panel.open({
        // Заголовок — имя папки у существующей (кадр iOS C04-chats-folder-page, Ч15), «Добавить
        // папку» у новой; подзаголовок «Чаты». Сохранение — галочкой шапки (ставит сам редактор).
        title: id ? (folders.find((f) => f.id === id)?.name ?? t('editTitle')) : t('add'),
        // ЛИСТ ПАПКИ — УРОВЕНЬ СТОПКИ НАД «ПАПКАМИ», НЕ ПОДМЕНА (кадры iOS C04-chats-folder-new,
        // C04-chats-folder-page, сверка Н3 03.10): слева «‹» назад к списку «Папки» (§11 —
        // вложенный слой ведёт назад стрелкой), список под ним остаётся живым. Имя уровня
        // (`winKey`) и делает его вложенным; без него лист подменял корень и стоял «×».
        ...(phone ? { subtitle: tc('sections.messages'), subStack: true, winKey: 'msgs-folder-edit' } : {}),
        sheet: true,
        body: <FolderEditor id={id} accounts={accounts} onDone={() => panel.back()} />,
      });
    },
    [panel, accounts, t, folders],
  );

  const openList = () => {
    setEditing('');
    setEditMode(false);
    setDraft(null);
    setStack([K.list]);
  };

  return { pages: [listPage, editPage, pickPage], applyTap, applySet, back, openList, openWebEditor };
}

/** СТРАНИЦА «ПАПКИ → ЧАТЫ» В ПОЛЕ «НАСТРОЕК» на широком экране (пакет Д1.2, владелец 04.10.2026;
 *  кадр iOS K09-settings → C04-chats-folders): тот же список папок с «Добавить папку», что лист
 *  «Папки» раздела, только на странице; папка открывается ПАНЕЛЬЮ-СТОПКОЙ 460 (тот же редактор,
 *  что у листа). Аккаунты — той же лёгкой ручкой, что у открывалки из «Настроек». */
export function ChatFoldersInline() {
  const { data } = usePersistentData<{ ok: boolean; accounts: DialogAccount[] }>(
    'msg-accounts',
    '/api/cabinet/messages/accounts',
  );
  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const [stack, setStack] = useState<string[]>([]);
  const core = useFolderPagesCore(accounts, { prefix: '', stack, setStack });
  return <FoldersList onEdit={core.openWebEditor} />;
}

/** Хозяин страниц папок в РАЗДЕЛЕ «ЧАТЫ»: свой родной лист (`msgsFoldersSheet`) в
 *  приложении, веб-шторка на сайте. */
export function useFoldersSettings(
  accounts: DialogAccount[],
  theme: 'light' | 'dark',
): { open: () => void } {
  const t = useTranslations('cabinet.messages.folders');
  const panel = useSlidePanel();
  const native = nativeFoldersSupported();
  /** Что открыто родной стопкой: [] — закрыто, ['folders'], ['folders','folder']. */
  const [stack, setStack] = useState<string[]>([]);
  const core = useFolderPagesCore(accounts, { prefix: '', stack, setStack });
  // СВЕЖАЯ РУКА РЕДАКТОРА (пакет Д2.2 п.11): лист «Папки» открывается однажды, а его тело
  // держало `openWebEditor` первого рендера — со списком аккаунтов из кэша или пустым. Папка
  // тогда получала старый свод, и выбранный аккаунт не находился по ключу. Тело зовёт руку через ref.
  const coreRef = useRef(core);
  coreRef.current = core;
  const data: NativeMsgsFoldersData | null = native && stack.length > 0 ? { theme, pages: core.pages, stack } : null;

  useNativePages<NativeMsgsFoldersData>({
    names: FOLDERS_NAMES,
    feed: 'msgs-folders',
    supported: native,
    data,
    onTap: (key) => core.applyTap(key),
    onSet: core.applySet,
    onBack: core.back,
  });

  const open = useCallback(() => {
    if (native) {
      core.openList();
      return;
    }
    const phone = readLayoutMode() === 'phone';
    panel.open({
      // Телефон (кадр iOS C04-chats-folders, ТЗ WEB-PARITY-3 Ч12): в шапке только «‹» и «Изм.»,
      // имя листа — героем в теле. Широкий экран (панель 460, пакет Д2.2 п.1/п.8): ОДНО имя в шапке —
      // «Папки чатов» (о каких папках речь, владелец 14.09.2026), под ним герой и карточка; прежние
      // заголовок «Папки» + подпись + герой давали тройное имя.
      title: phone ? '' : t('whose'),
      sheet: true,
      // ПОЛУШТОРКА С РАЗВОРОТОМ (APP-CLAIMS-1020 ред. 11, Д6 / 10.9; слово владельца 21.10.2026: «папки в трёх местах —
      // полушторками; потом её можно развернуть в стопку»; страница сервера chats.ts `settingsPresent: 'half'`): на
      // телефоне лист встаёт на половину экрана (детент 0.5) и разворачивается до полного первым касанием (drawer.tsx,
      // `grown`); «×» — полулист, представленный поверх (ТЗ WEB-CAUSES М83). Из полосы папок и из «Настроек» — одна рука.
      ...(phone ? { sheetDetent: 0.5, lead: 'close' as const } : {}),
      body: <FoldersList onEdit={(id) => coreRef.current.openWebEditor(id)} />,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- core пересобирается каждый рендер, руки внутри свежие
  }, [native, panel, t]);

  return { open };
}

/** Аккаунты переписок для страниц папок вне раздела «Чаты» (П107): тот же свод, что у
 *  списка чатов, отдельной лёгкой ручкой. */
const MSG_ACCOUNTS_URL = '/api/cabinet/messages/accounts';

/**
 * ХОЗЯИН СТРАНИЦ ПАПОК «ЧАТОВ» В СТОПКЕ «НАСТРОЕК» (П107, владелец 13.09.2026): «Папки →
 * Чаты» кладёт страницы папок в ту же стопку `settings`, без смены вкладки и без раздела
 * чатов. Стопка — часть стопки настроек (ключи с префиксом `page:fld:`), тапы и
 * значения — те же `fld:*`.
 */
export const CHAT_FOLDERS_PAGE = 'page:fld:';
export function useNativeChatFoldersPages(settingsStack: string[]): {
  pages: NativeAccountPage[];
  tapRow: (key: string) => boolean;
  applySet: (key: string, value: string | boolean) => boolean;
  openList: () => void;
} {
  const inStack = settingsStack.some((k) => k.startsWith(CHAT_FOLDERS_PAGE));
  const { data } = usePersistentData<{ ok: boolean; accounts: DialogAccount[] }>(
    'msg-accounts',
    inStack ? MSG_ACCOUNTS_URL : '',
  );
  const stack = settingsStack.filter((k) => k.startsWith(CHAT_FOLDERS_PAGE));
  const setStack = (next: string[]) => {
    if (next.length === 0) {
      closeNativeSettingsPage(`${CHAT_FOLDERS_PAGE}folders`);
      return;
    }
    openNativeSettingsPage(['settings', ...next]);
  };
  const core = useFolderPagesCore(data?.accounts ?? [], { prefix: CHAT_FOLDERS_PAGE, stack, setStack });
  // «Назад» пальцем стопка настроек делает сама; черновик бросаем, когда редактор ушёл.
  const wasEdit = useRef(false);
  const editOpen = stack.includes(`${CHAT_FOLDERS_PAGE}folder`);
  useEffect(() => {
    if (wasEdit.current && !editOpen) core.back(`${CHAT_FOLDERS_PAGE}folder`);
    wasEdit.current = editOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- core свежий каждый рендер
  }, [editOpen]);
  return {
    pages: inStack ? core.pages : [],
    tapRow: (key) => {
      if (!key.startsWith('fld:')) return false;
      core.applyTap(key);
      return true;
    },
    applySet: (key, value) => {
      if (!key.startsWith('fld:')) return false;
      core.applySet(key, value);
      return true;
    },
    openList: core.openList,
  };
}
